import { ReactNode } from "react";
import { cn } from "src/utils/cn";

export type BadgeTextSize = "default" | "sm" | "xs";

export type BadgeRadius = "full" | "md" | "default";

export type BadgeVariant =
  | "primary"
  | "secondary"
  | "success"
  | "info"
  | "light"
  | "muted";

export type BadgeProps = React.HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  variant?: BadgeVariant;
  radius?: BadgeRadius;
  textSize?: BadgeTextSize;
  "data-test"?: string;
};

const variantStyles: Record<BadgeVariant, string> = {
  primary: `text-white bg-red-500`,
  secondary: `text-white bg-gray-700`,
  success: `text-white bg-green-500`,
  info: `text-white bg-blue-500`,
  light: `text-black bg-white`,
  muted: `text-gray-500 bg-gray-200`,
};

const radiusStyles: Record<BadgeRadius, string> = {
  full: `rounded-full`,
  md: `rounded-md`,
  default: `rounded`,
};

const textSizeStyles: Record<BadgeTextSize, string> = {
  xs: `text-xs leading-none px-2`,
  sm: `text-sm leading-none px-2`,
  default: `text-base px-3`,
};
// To prevent the text from misaligning inside flex containers, we're including inline-flex.
const baseStyles = `py-1 font-medium inline-flex items-center`;

export const Badge = ({
  variant = "primary",
  radius = "default",
  textSize = "default",
  children,
  "data-test": dataTest,
  className,
  ...props
}: BadgeProps) => (
  <div
    className={cn(
      baseStyles,
      variantStyles[variant],
      radiusStyles[radius],
      textSizeStyles[textSize],
      className
    )}
    data-test={dataTest}
    {...props}
  >
    {children}
  </div>
);
